import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';

const STORAGE_KEY = 'stayfinder.admin.session';

export interface Session {
  accessToken: string;
  refreshToken: string;
}

export const session = {
  read(): Session | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as Session) : null;
    } catch {
      return null;
    }
  },
  write(value: Session) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
      /* ignore */
    }
  },
  clear() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  },
};

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const current = session.read();
  if (current?.accessToken) config.headers.Authorization = `Bearer ${current.accessToken}`;
  return config;
});

let onExpired: (() => void) | null = null;
export const setExpiredHandler = (handler: () => void) => {
  onExpired = handler;
};

// One shared refresh, so concurrent 401s do not replay the refresh token against each
// other - the API treats a reused refresh token as theft and burns the whole session.
let refreshing: Promise<string | null> | null = null;

async function refresh(): Promise<string | null> {
  const current = session.read();
  if (!current?.refreshToken) return null;

  try {
    const { data } = await axios.post<{ accessToken: string; refreshToken: string }>(
      `${import.meta.env.VITE_API_BASE_URL || ''}/api/auth/refresh`,
      { refreshToken: current.refreshToken },
    );

    session.write({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    return data.accessToken;
  } catch {
    session.clear();
    return null;
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };

    if (error.response?.status === 401 && original && !original._retried && !original.url?.includes('/api/auth/')) {
      original._retried = true;
      refreshing ??= refresh().finally(() => {
        refreshing = null;
      });

      const token = await refreshing;
      if (token) {
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      }

      onExpired?.();
    }

    return Promise.reject(error);
  },
);

/**
 * What kind of failure this was.
 *
 * Same vocabulary as the customer and partner clients, so the three apps describe failures
 * identically even though each has its own HTTP layer. The distinction decides what the
 * screen does: a dead connection deserves a retry, a 403 does not, and neither may be
 * rendered as "no records".
 */
export type ApiErrorKind =
  | 'network'
  | 'timeout'
  | 'validation'
  | 'auth'
  | 'forbidden'
  | 'notFound'
  | 'conflict'
  | 'server'
  | 'unknown';

export interface ApiError {
  message: string;
  status?: number;
  kind: ApiErrorKind;
  /** Whether trying the same request again could plausibly succeed. */
  retryable: boolean;
}

function kindForStatus(status: number | undefined): ApiErrorKind {
  if (status === undefined) return 'unknown';
  if (status === 401) return 'auth';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  if (status === 409) return 'conflict';
  if (status === 422 || status === 400) return 'validation';
  if (status >= 500) return 'server';
  return 'unknown';
}

const RETRYABLE: ReadonlySet<ApiErrorKind> = new Set(['network', 'timeout', 'server']);

/**
 * The classified form of {@link errorMessage}.
 *
 * Added alongside rather than replacing it: dozens of call sites only need the sentence,
 * and rewriting them all to unwrap an object would be churn for no gain. Screens that
 * render a whole failure state use this instead.
 */
export function classifyError(error: unknown): ApiError {
  const message = errorMessage(error);

  if (axios.isAxiosError(error)) {
    if (error.code === 'ECONNABORTED') {
      return { message, kind: 'timeout', retryable: true };
    }

    if (!error.response) {
      return {
        message: 'We could not reach the API. Check your connection and try again.',
        kind: 'network',
        retryable: true,
      };
    }

    const kind = kindForStatus(error.response.status);
    return { message, status: error.response.status, kind, retryable: RETRYABLE.has(kind) };
  }

  return { message, kind: 'unknown', retryable: false };
}

export function errorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const problem = error.response?.data as { detail?: string; title?: string; errors?: Record<string, string[]> };

    if (problem?.errors) {
      const first = Object.values(problem.errors)[0]?.[0];
      if (first) return first;
    }

    if (problem?.detail) return problem.detail;
    if (!error.response) return 'Could not reach the API.';

    return problem?.title ?? `Request failed (${error.response.status}).`;
  }

  return 'Something went wrong.';
}

export function toQuery(params: Record<string, unknown>): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.append(key, String(value));
  }

  const query = search.toString();
  return query ? `?${query}` : '';
}

// =====================================================================================
// Types
// =====================================================================================

export interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
}

export interface AdminDashboard {
  totalUsers: number;
  totalCustomers: number;
  totalOwners: number;
  suspendedAccounts: number;
  newUsersLast30Days: number;
  totalBusinesses: number;
  totalProperties: number;
  pgBranches: number;
  homeListings: number;
  hotelBranches: number;
  activeListings: number;
  pendingVerification: number;
  expiredListings: number;
  suspendedListings: number;
  draftListings: number;
  activeSubscriptions: number;
  subscriptionsExpiringIn7Days: number;
  paidHomeListings: number;
  successfulPayments: number;
  failedPayments: number;
  pendingPaymentRecords: number;
  freeActivations: number;
  paidSubscriptions: number;
  paymentConfiguration: { category: string; paymentEnabled: boolean; amount: number; currency: string }[];

  /** Organisations currently entitled to run more than one branch. */
  multiBranchOrganisations: number;

  /** Of those, how many were waived because payment is switched off. */
  multiBranchFreeGrants: number;

  multiBranchEnabled: boolean;
  multiBranchPaymentRequired: boolean;
  grossCollections: number;
  netRevenue: number;
  taxCollected: number;
  refundedAmount: number;
  currency: string;
  simulatedPaymentCount: number;
  openReports: number;
  pendingReviews: number;
  recentActivity: {
    atUtc: string;
    action: string;
    entityType: string;
    entityId?: string;
    actor?: string;
    summary?: string;
  }[];
}

export interface AdminUser {
  id: string;
  fullName: string;
  email: string;
  phone?: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  contactVerifiedByAdmin: boolean;
  status: string;
  statusReason?: string;
  roles: string[];
  businessCount: number;
  propertyCount: number;
  createdAtUtc: string;
  lastLoginAtUtc?: string;
}

export interface AdminBusiness {
  id: string;
  name: string;
  slug: string;
  status: string;
  isVerified: boolean;
  isArchived: boolean;
  ownerUserId: string;
  ownerName: string;
  ownerEmail: string;
  branchCount: number;
  staffAccountCount: number;
  activeStaffAccountCount: number;
  multiBranchFeatureEnabled: boolean;
  multiBranchPaymentRequired: boolean;
  multiBranchEntitlementStatus: string;
  multiBranchEntitlementSource?: string;
  multiBranchEntitlementExpiryUtc?: string;
}

export interface AdminBusinessDetail {
  business: AdminBusiness;
  description?: string;
  contactPersonName?: string;
  contactPhone: string;
  contactEmail?: string;
  branches: AdminBranch[];
  entitlements: AdminEntitlement[];
}

export interface AdminBranch {
  id: string;
  businessId: string;
  businessName: string;
  name: string;
  category: string;
  locality: string;
  city: string;
  address: string;
  status: string;
  statusReason?: string;
  isArchived: boolean;
  staff: AdminBranchStaff[];
  subscriptions: AdminBranchSubscription[];
}

export interface AdminBranchStaff {
  userId: string;
  membershipId: string;
  assignmentId: string;
  fullName: string;
  username: string;
  accountStatus: string;
  membershipIsActive: boolean;
  assignmentIsActive: boolean;
  assignedAtUtc: string;
  removedAtUtc?: string;
}

export interface AdminBranchSubscription {
  id: string;
  listingPlanId?: string;
  planCode: string;
  storedStatus: string;
  currentStatus: string;
  startUtc?: string;
  expiryUtc?: string;
  graceEndsUtc?: string;
  totalAmount: number;
  currency: string;
}

export interface AdminEntitlement {
  id: string;
  storedStatus: string;
  currentStatus: string;
  source: string;
  startUtc?: string;
  expiryUtc?: string;
  graceEndsUtc?: string;
  totalAmount: number;
  currency: string;
  permitsMultipleBranchesNow: boolean;
}

export interface AdminStaff {
  userId: string;
  fullName: string;
  email: string;
  phone?: string;
  accountStatus: string;
  createdAtUtc: string;
  lastLoginAtUtc?: string;
  memberships: AdminStaffMembership[];
}

export interface AdminStaffMembership {
  membershipId: string;
  businessId: string;
  businessName: string;
  role: string;
  isActive: boolean;
  username: string;
  assignments: AdminStaffAssignment[];
}

export interface AdminStaffAssignment {
  assignmentId: string;
  branchId: string;
  branchName: string;
  isActive: boolean;
  assignedAtUtc: string;
  removedAtUtc?: string;
}

export interface AdminProperty {
  id: string;
  title: string;
  branchName?: string;
  category: string;
  businessId: string;
  businessName: string;
  ownerName: string;
  ownerEmail: string;
  locality: string;
  status: string;
  statusReason?: string;
  priceFrom?: number;
  photoCount: number;
  openReportCount: number;
  hasValidBilling: boolean;
  submittedAtUtc?: string;
  publishedAtUtc?: string;
  expiresAtUtc?: string;
  lastAvailabilityUpdateUtc?: string;
  createdAtUtc: string;
}

export interface AdminReport {
  id: string;
  propertyId: string;
  propertyTitle: string;
  propertyStatus: string;
  reason: string;
  details?: string;
  reporterName?: string;
  reporterContact?: string;
  status: string;
  resolutionNotes?: string;
  createdAtUtc: string;
  resolvedAtUtc?: string;
}

export interface ListingPlan {
  id: string;
  code: string;
  version: number;
  name: string;
  description?: string;
  category?: string;
  billingType: string;
  promotionalDiscountPercentage: number;
  promotionEndsAtUtc?: string;
  listPrice: number;
  discountAmount: number;
  amount: number;
  taxPercentage: number;
  taxAmount: number;
  totalAmount: number;
  currency: string;
  durationDays: number;
  features: string[];
  isDefault: boolean;
  isActive: boolean;
  displayOrder: number;
}

/** One category's payment rules. The server is the only place these are decided. */
export type PaymentConfigurationCategory =
  | 'HostelSubscription'
  | 'HotelSubscription'
  | 'HomeListingFee'
  | 'MultiBranchSubscription';

export interface PaymentConfiguration {
  id: string;
  category: PaymentConfigurationCategory;

  /** Null for the organisation-level entitlement, which governs no category. */
  accommodationCategory?: string;

  paymentEnabled: boolean;

  /**
   * Whether the capability is offered at all, as distinct from whether it is charged
   * for. Only meaningful for the multi-branch entitlement.
   */
  featureEnabled: boolean;

  amount: number;
  taxPercentage: number;
  totalAmount: number;
  billingPeriod: 'Monthly' | 'OneTime' | 'Yearly';
  listingValidityDays: number;

  /** Days after expiry during which the thing keeps working. */
  gracePeriodDays: number;

  currency: string;
  managedPlanCode: string;
  updatedAtUtc?: string;
  updatedByUserId?: string;

  /** Live on a real captured payment. Organisations, for the entitlement. */
  paidActiveCount: number;

  /** Live without one, because payment is switched off. */
  freeActivationCount: number;
}

export interface PaymentConfigurationWrite {
  paymentEnabled: boolean;
  featureEnabled: boolean;
  amount: number;
  taxPercentage: number;
  listingValidityDays: number;
  gracePeriodDays: number;
  currency: string;
}

export interface PlatformSetting {
  key: string;
  value: string;
  valueType: string;
  description?: string;
  group: string;
  isPublic: boolean;
  updatedAtUtc?: string;
}

export interface RevenueBucket {
  label: string;
  grossCollections: number;
  netRevenue: number;
  paymentCount: number;
}

export interface FinancialReport {
  from: string;
  to: string;
  currency: string;
  grossCollections: number;
  taxCollected: number;
  refundedAmount: number;
  netRevenue: number;
  successfulPaymentCount: number;
  failedPaymentCount: number;
  refundCount: number;
  byCategory: RevenueBucket[];
  byBillingType: RevenueBucket[];
  byMonth: RevenueBucket[];
  activeSubscriptions: number;
  expiredSubscriptions: number;
  upcomingRenewals: number;
  caveat?: string;
}

export interface AuditLog {
  id: string;
  actorUserId?: string;
  actorEmail?: string;
  actorRole?: string;
  action: string;
  entityType: string;
  entityId?: string;
  summary?: string;
  beforeJson?: string;
  afterJson?: string;
  ipAddress?: string;
  createdAtUtc: string;
}

export interface UserProfile {
  id: string;
  fullName: string;
  email: string;
  roles: string[];
}

// =====================================================================================
// Endpoints
// =====================================================================================

export const adminApi = {
  login: (emailOrPhone: string, password: string) =>
    api
      .post<{ accessToken: string; refreshToken: string; user: UserProfile }>('/api/auth/login', {
        emailOrPhone,
        password,
      })
      .then((r) => r.data),

  /**
   * Registers a Super Admin. The admin code is validated on the server; the expected
   * value is never sent to or held by this application.
   */
  registerAdmin: (body: {
    adminCode: string;
    fullName: string;
    email: string;
    phone?: string;
    password: string;
    confirmPassword: string;
  }) =>
    api
      .post<{ accessToken: string; refreshToken: string; user: UserProfile }>(
        '/api/auth/register-admin',
        body,
      )
      .then((r) => r.data),

  me: () => api.get<UserProfile>('/api/auth/me').then((r) => r.data),

  dashboard: () => api.get<AdminDashboard>('/api/admin/dashboard').then((r) => r.data),

  users: (params: { keyword?: string; role?: string; status?: string; page?: number; pageSize?: number }) =>
    api.get<PagedResult<AdminUser>>(`/api/admin/users${toQuery(params)}`).then((r) => r.data),

  businesses: (params: { keyword?: string; page?: number; pageSize?: number }) =>
    api.get<PagedResult<AdminBusiness>>(`/api/admin/businesses${toQuery(params)}`).then((r) => r.data),

  business: (businessId: string) =>
    api.get<AdminBusinessDetail>(`/api/admin/businesses/${businessId}`).then((r) => r.data),

  branch: (branchId: string) =>
    api.get<AdminBranch>(`/api/admin/branches/${branchId}`).then((r) => r.data),

  staff: (params: { keyword?: string; page?: number; pageSize?: number }) =>
    api.get<PagedResult<AdminStaff>>(`/api/admin/staff${toQuery(params)}`).then((r) => r.data),

  staffDetail: (userId: string) =>
    api.get<AdminStaff>(`/api/admin/staff/${userId}`).then((r) => r.data),

  suspendUser: (userId: string, reason: string) =>
    api.post(`/api/admin/users/${userId}/suspend`, { reason }),

  reactivateUser: (userId: string) => api.post(`/api/admin/users/${userId}/reactivate`),

  /**
   * Changes the signed-in administrator's own password.
   *
   * Both values go straight into the request body over HTTPS and are hashed on the
   * server. Nothing here writes either one to the console, to localStorage, or to any
   * error message, so a new password never survives the call.
   */
  changeOwnPassword: (body: { currentPassword: string; newPassword: string }) =>
    api.post('/api/auth/change-password', body).then(() => undefined),

  verifyContact: (userId: string, businessId?: string) =>
    api.post(`/api/admin/users/${userId}/verify-contact${toQuery({ businessId })}`),

  properties: (params: {
    category?: string;
    status?: string;
    keyword?: string;
    locality?: string;
    reported?: boolean;
    page?: number;
    pageSize?: number;
  }) => api.get<PagedResult<AdminProperty>>(`/api/admin/properties${toQuery(params)}`).then((r) => r.data),

  approve: (propertyId: string, reason?: string) =>
    api.post(`/api/admin/properties/${propertyId}/approve`, { reason }),

  reject: (propertyId: string, reason: string, internalNotes?: string) =>
    api.post(`/api/admin/properties/${propertyId}/reject`, { reason, internalNotes }),

  requestCorrection: (propertyId: string, reason: string) =>
    api.post(`/api/admin/properties/${propertyId}/request-correction`, { reason }),

  suspendListing: (propertyId: string, reason: string) =>
    api.post(`/api/admin/properties/${propertyId}/suspend`, { reason }),

  reinstate: (propertyId: string, reason?: string) =>
    api.post(`/api/admin/properties/${propertyId}/reinstate`, { reason }),

  archiveListing: (propertyId: string, reason?: string) =>
    api.post(`/api/admin/properties/${propertyId}/archive`, { reason }),

  verifyStarRating: (propertyId: string, starRating: number | undefined, verified: boolean, notes?: string) =>
    api.post(`/api/admin/properties/${propertyId}/verify-star-rating`, { starRating, verified, notes }),

  reports: (params: { status?: string; page?: number; pageSize?: number }) =>
    api.get<PagedResult<AdminReport>>(`/api/admin/reports${toQuery(params)}`).then((r) => r.data),

  resolveReport: (reportId: string, status: string, resolutionNotes?: string) =>
    api.post(`/api/admin/reports/${reportId}/resolve`, { status, resolutionNotes }),

  plans: () => api.get<ListingPlan[]>('/api/admin/plans').then((r) => r.data),

  deletePlan: (code: string) => api.delete(`/api/admin/plans/${encodeURIComponent(code)}`),

  savePlan: (body: {
    code: string;
    name: string;
    description?: string;
    category?: string;
    billingType: string;
    amount: number;
    taxPercentage: number;
    durationDays: number;
    promotionalDiscountPercentage: number;
    promotionEndsAtUtc: string | null;
    features?: string;
    isDefault: boolean;
    isActive: boolean;
    displayOrder: number;
  }) => api.post<ListingPlan>('/api/admin/plans', body).then((r) => r.data),

  paymentConfiguration: () =>
    api.get<PaymentConfiguration[]>('/api/admin/payment-configuration').then((r) => r.data),

  savePaymentConfiguration: (category: string, body: PaymentConfigurationWrite) =>
    api.put<PaymentConfiguration>(`/api/admin/payment-configuration/${category}`, body).then((r) => r.data),

  settings: () => api.get<PlatformSetting[]>('/api/admin/settings').then((r) => r.data),

  updateSetting: (key: string, value: string) => api.put('/api/admin/settings', { key, value }),

  financialReport: (params: { from?: string; to?: string; includeSimulated?: boolean }) =>
    api.get<FinancialReport>(`/api/admin/reports/financial${toQuery(params)}`).then((r) => r.data),

  auditLogs: (params: {
    action?: string;
    entityType?: string;
    entityId?: string;
    from?: string;
    to?: string;
    page?: number;
    pageSize?: number;
  }) => api.get<PagedResult<AuditLog>>(`/api/admin/audit-logs${toQuery(params)}`).then((r) => r.data),
};

// =====================================================================================
// Formatting
// =====================================================================================

const INR = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
const INR_PAISE = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
});

export function money(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return '—';
  return Math.round(amount * 100) % 100 === 0 ? INR.format(amount) : INR_PAISE.format(amount);
}

/** All stored timestamps are UTC; they are shown in India Standard Time. */
export function dateTime(utc: string | null | undefined): string {
  if (!utc) return '—';
  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'Asia/Kolkata',
  }).format(new Date(utc));
}

export function date(utc: string | null | undefined): string {
  if (!utc) return '—';
  return new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeZone: 'Asia/Kolkata' }).format(new Date(utc));
}

export function humanise(value: string | null | undefined): string {
  if (!value) return '';
  const spaced = value.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

export function statusVariant(status: string): string {
  switch (status) {
    case 'Active':
      return 'success';
    case 'ExpiringSoon':
    case 'PendingPayment':
      return 'warning';
    case 'PendingReview':
      return 'info';
    case 'Rejected':
    case 'Suspended':
    case 'Expired':
      return 'danger';
    default:
      return 'secondary';
  }
}
